import { useEffect, useState } from 'react';
import { Loader2, Package, Plus, Save, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { bearerAuthHeader } from '../utils/apiHeaders';
import { COMMON_ATLASSIAN_APPS, normalizeApps, type AppType, type CompanyApp } from '../utils/companyProfile';

/**
 * Company Settings › Apps and licenses (RESTORE_PLAN R3.3, the Apps & Licensing
 * part of the old CompanyProfileEditor): the Atlassian and Marketplace apps the
 * client runs and how many licenses of each, added from the common Atlassian
 * apps or typed, edited and removed. Saved on the company (`PATCH
 * /companies/:id { apps }`). Renewal dates, costs and contacts across all
 * clients are the firm's license portfolio (Firm › Licenses).
 */

const API = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
const OTHER = '__other__';

export function CompanyAppsCard({ companyId, firmSide }: { companyId: string; firmSide: boolean }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<CompanyApp[]>([]);
  const [apps, setApps] = useState<CompanyApp[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState<string>(COMMON_ATLASSIAN_APPS[0]);
  const [typedName, setTypedName] = useState('');
  const [newType, setNewType] = useState<AppType>('atlassian');
  const [newCount, setNewCount] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/companies/${companyId}/profile`, { headers: { ...bearerAuthHeader() } });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Could not load the apps');
        if (!live) return;
        const list: CompanyApp[] = Array.isArray(body.profile?.apps) ? body.profile.apps : [];
        setSaved(list);
        setApps(list);
        setCanEdit(!!body.canEdit);
      } catch (e: any) {
        if (live) toast.error(e?.message || 'Could not load the apps');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [companyId]);

  const dirty = JSON.stringify(apps) !== JSON.stringify(saved);

  const addApp = () => {
    const name = (pick === OTHER ? typedName : pick).trim();
    if (!name) {
      toast.error('App name is required');
      return;
    }
    const count = newCount === '' ? 0 : Number(newCount);
    if (!Number.isInteger(count) || count < 0) {
      toast.error('License count must be a whole number, 0 or more');
      return;
    }
    setApps([...apps, { id: `app_${Date.now()}`, name, type: pick === OTHER ? newType : 'atlassian', licenseCount: count }]);
    setAdding(false);
    setTypedName('');
    setNewCount('');
  };

  const save = async () => {
    const checked = normalizeApps(apps);
    if (!checked.ok) {
      toast.error(checked.error);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}`, {
        method: 'PATCH',
        headers: { ...bearerAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ apps: checked.apps }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not save the apps');
      const list: CompanyApp[] = body.profile?.apps ?? checked.apps;
      setSaved(list);
      setApps(list);
      toast.success('Apps and licenses saved');
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the apps');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading apps" />
        </CardContent>
      </Card>
    );
  }

  const total = apps.reduce((n, a) => n + (Number(a.licenseCount) || 0), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="size-5 text-blue-500" aria-hidden="true" />
              Apps and licenses
            </CardTitle>
            <CardDescription>
              {apps.length ? `${apps.length} ${apps.length === 1 ? 'app' : 'apps'}, ${total} licenses` : 'The Atlassian and Marketplace apps this client runs'}
            </CardDescription>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {dirty && <Button variant="outline" onClick={() => setApps(saved)} disabled={saving}>Cancel</Button>}
              {dirty && (
                <Button onClick={save} disabled={saving}>
                  {saving ? <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" /> : <Save className="size-4 mr-2" aria-hidden="true" />}
                  Save apps
                </Button>
              )}
              {!adding && (
                <Button variant="outline" onClick={() => setAdding(true)}>
                  <Plus className="size-4 mr-2" aria-hidden="true" />
                  Add App
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {apps.length === 0 && !adding && (
          <Alert>
            <Package className="size-4" />
            <AlertDescription>No apps yet. Add the Atlassian and Marketplace apps the client uses, with license counts.</AlertDescription>
          </Alert>
        )}
        {apps.map((app) => (
          <div key={app.id} className="flex items-center gap-3 p-3 border rounded-lg" data-testid="company-app">
            <Package className="size-4 text-gray-400" aria-hidden="true" />
            <div className="flex-1 flex items-center gap-2">
              <span>{app.name}</span>
              <Badge variant={app.type === 'atlassian' ? 'default' : 'secondary'}>{app.type === 'atlassian' ? 'Atlassian' : 'Marketplace'}</Badge>
            </div>
            <Label htmlFor={`licenses-${app.id}`} className="text-muted-foreground">Licenses</Label>
            <Input
              id={`licenses-${app.id}`}
              type="number"
              min={0}
              className="w-24"
              value={app.licenseCount}
              disabled={!canEdit}
              onChange={(e) => setApps(apps.map((a) => (a.id === app.id ? { ...a, licenseCount: parseInt(e.target.value, 10) || 0 } : a)))}
            />
            {canEdit && (
              <Button size="sm" variant="ghost" aria-label={`Remove ${app.name}`} onClick={() => setApps(apps.filter((a) => a.id !== app.id))}>
                <Trash2 className="size-4 text-red-500" aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}

        {adding && (
          <div className="p-4 border rounded-lg bg-gray-50 space-y-3">
            <div className="flex items-center justify-between">
              <h4>Add an app</h4>
              <Button size="sm" variant="ghost" aria-label="Close" onClick={() => setAdding(false)}><X className="size-4" aria-hidden="true" /></Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="new-app">App</Label>
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger id="new-app" aria-label="App"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMMON_ATLASSIAN_APPS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    <SelectItem value={OTHER}>Other (type it)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {pick === OTHER && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-app-name">App name</Label>
                    <Input id="new-app-name" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="e.g. ScriptRunner" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-app-type">Type</Label>
                    <Select value={newType} onValueChange={(v) => setNewType(v as AppType)}>
                      <SelectTrigger id="new-app-type" aria-label="Type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="atlassian">Atlassian</SelectItem>
                        <SelectItem value="marketplace">Marketplace</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-app-licenses">Licenses</Label>
                <Input id="new-app-licenses" type="number" min={0} value={newCount} onChange={(e) => setNewCount(e.target.value)} placeholder="0" />
              </div>
            </div>
            <Button onClick={addApp}><Plus className="size-4 mr-2" aria-hidden="true" />Add</Button>
          </div>
        )}

        {firmSide && (
          <p className="text-muted-foreground pt-2">Renewal dates, costs and contacts for every client are in Firm › Licenses.</p>
        )}
      </CardContent>
    </Card>
  );
}
